import { Spinner } from '@/components/glitch-ui/spinner';
import { createFileRoute } from '@tanstack/react-router';
import {
  Boxes, Plus, Search, Download, Filter, Settings, Edit, Trash2, X, CheckCircle,
  AlertCircle, Info, ArrowRight, ArrowLeft
} from 'lucide-react';
import { ComponentTabs } from '@/components/glitch-ui/component-tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { GoogleButton } from '@/components/glitch-ui/google-button';
import { AttentionBadge } from '@/components/glitch-ui/attention-badge';

export const Route = createFileRoute('/_authenticated/_layout/components/buttons')({
  component: ButtonsPage,
  staticData: {
    title: 'Components',
    icon: Boxes,
  },
});

function ButtonsPage() {
  return (
    <div className="flex flex-col h-full">
      <ComponentTabs />

      <div className="flex-1 pb-8 space-y-8">
        <section>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Buttons</h2>

          {/* Button Colour Hierarchy */}
          <Card className="rounded-lg border bg-white mb-6">
            <CardHeader className="p-6 pb-1">
              <CardTitle className="text-lg font-semibold text-gray-900">Button Colour Hierarchy</CardTitle>
              <p className="text-gray-900">Strict colour rules for buttons across the app. Never use indigo for buttons.</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3">
                  <div className="w-32 h-9 rounded-md bg-[#290BD9] flex items-center justify-center text-white text-xs font-medium">Cobalt #290BD9</div>
                  <span className="text-gray-900">All primary actions: Save, Approve, Submit, Create, Connect, Next</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-32 h-9 rounded-md bg-green-600 flex items-center justify-center text-white text-xs font-medium">Green</div>
                  <span className="text-gray-900">GO LIVE / Deploy only. Never for Save Changes.</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-32 h-9 rounded-md bg-red-700 flex items-center justify-center text-white text-xs font-medium">Red</div>
                  <span className="text-gray-900">Destructive: Delete confirmations only</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-32 h-9 rounded-md border border-gray-200 bg-white flex items-center justify-center text-gray-900 text-xs font-medium">Outline</div>
                  <span className="text-gray-900">Secondary: Cancel, Back, toolbar actions (filter, export)</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-32 h-9 rounded-md bg-transparent flex items-center justify-center text-gray-900 text-xs font-medium">Ghost</div>
                  <span className="text-gray-900">Tertiary: Dismiss, Reset, minor actions</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Primary Action Buttons */}
          <Card className="rounded-lg border bg-white mb-6">
            <CardHeader className="p-6 pb-1">
              <CardTitle className="text-lg font-semibold text-gray-900">Primary Action Buttons</CardTitle>
              <p className="text-gray-900">Main CTAs with brand Cobalt color and arrow icon (size="lg" + py-6)</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-xs text-gray-500 mb-2">Primary Forward Actions</p>
                <div className="flex flex-wrap gap-3">
                  <Button
                    size="lg"
                    className="w-[280px] py-6 bg-[#290BD9] hover:bg-[#2309C5] text-white shadow-lg flex items-center justify-center space-x-2"
                  >
                    <span>CREATE CAMPAIGN</span>
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                  <Button
                    size="lg"
                    disabled
                    className="w-[280px] py-6 bg-[#290BD9] hover:bg-[#2309C5] text-white shadow-lg flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span>DISABLED STATE</span>
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                  <Button
                    size="lg"
                    disabled
                    className="w-[280px] py-6 bg-[#290BD9] hover:bg-[#2309C5] text-white shadow-lg flex items-center justify-center space-x-2"
                  >
                    <Spinner size="sm" />
                    <span>PROCESSING...</span>
                  </Button>
                </div>
              </div>

              <div>
                <p className="text-xs text-gray-500 mb-2">Back Button (Outline variant)</p>
                <div className="flex flex-wrap gap-3">
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-[216px] py-6 flex items-center justify-center space-x-2 text-gray-700 hover:text-gray-900 border-gray-200 bg-white hover:bg-gray-50"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    <span>BACK</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    disabled
                    className="w-[216px] py-6 flex items-center justify-center space-x-2 text-gray-700 hover:text-gray-900 border-gray-200 bg-white hover:bg-gray-50"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    <span>BACK</span>
                  </Button>
                </div>
              </div>

              <div>
                <p className="text-xs text-gray-500 mb-2">Launch Button (Unique green - bg-green-600)</p>
                <div className="flex flex-wrap gap-3">
                  <Button
                    size="lg"
                    className="w-[280px] py-6 bg-green-600 hover:bg-green-700 text-white shadow-lg flex items-center justify-center space-x-2"
                  >
                    <span>GO LIVE</span>
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                  <Button
                    size="lg"
                    disabled
                    className="w-[280px] py-6 bg-green-600 hover:bg-green-700 text-white shadow-lg flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span>GO LIVE</span>
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                  <Button
                    size="lg"
                    disabled
                    className="w-[280px] py-6 bg-green-600 hover:bg-green-700 text-white shadow-lg flex items-center justify-center space-x-2"
                  >
                    <Spinner size="sm" />
                    <span>LAUNCHING...</span>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Semantic Color Buttons */}
          <Card className="rounded-lg border bg-white mb-6">
            <CardHeader className="p-6 pb-1">
              <CardTitle className="text-lg font-semibold text-gray-900">Semantic Color Buttons</CardTitle>
              <p className="text-gray-900">Contextual buttons using brand semantic colors</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Primary */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Primary</p>
                <p className="text-xs text-gray-500 mb-3">Main actions using brand Cobalt (#290BD9) - Size: 36px</p>
                <div className="flex flex-wrap gap-3">
                  <Button className="bg-[#290BD9] hover:bg-[#2309C5] text-white">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Item
                  </Button>
                  <Button className="bg-[#290BD9] hover:bg-[#2309C5] text-white">Normal</Button>
                  <Button className="bg-[#290BD9] hover:bg-[#2309C5] text-white" disabled>Disabled</Button>
                  <Button className="bg-[#290BD9] hover:bg-[#2309C5] text-white" disabled>
                    <Spinner size="sm" className="mr-2" />
                    Loading
                  </Button>
                </div>
              </div>

              <Separator />

              {/* Secondary */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Secondary</p>
                <p className="text-xs text-gray-500 mb-3">Alternative actions with subtle border - Size: 36px</p>
                <div className="flex flex-wrap gap-3">
                  <Button variant="outline">
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                  <Button variant="outline">Normal</Button>
                  <Button variant="outline" disabled>Disabled</Button>
                  <Button variant="outline" disabled>
                    <Spinner size="sm" className="mr-2" />
                    Loading
                  </Button>
                </div>
              </div>

              <Separator />

              {/* Tertiary */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Tertiary</p>
                <p className="text-xs text-gray-500 mb-3">Subtle actions with no background - Size: 36px</p>
                <div className="flex flex-wrap gap-3">
                  <Button variant="ghost">
                    <Search className="h-4 w-4 mr-2" />
                    Search
                  </Button>
                  <Button variant="ghost">Normal</Button>
                  <Button variant="ghost" disabled>Disabled</Button>
                  <Button variant="ghost" disabled>
                    <Spinner size="sm" className="mr-2" />
                    Loading
                  </Button>
                </div>
              </div>

              <Separator />

              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Success</p>
                <p className="text-xs text-gray-500 mb-3">Confirm actions, successful operations (bg: #D1FAE5, text: #047857, border: #6EE7B7) - Size: 36px</p>
                <div className="flex flex-wrap gap-3">
                  <Button className="bg-[#D1FAE5] hover:bg-[#C5F3DD] text-[#047857] border border-[#6EE7B7] shadow-none">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                  <Button className="bg-[#D1FAE5] hover:bg-[#C5F3DD] text-[#047857] border border-[#6EE7B7] shadow-none">Normal</Button>
                  <Button className="bg-[#D1FAE5] hover:bg-[#C5F3DD] text-[#047857] border border-[#6EE7B7] shadow-none" disabled>Disabled</Button>
                  <Button className="bg-[#D1FAE5] hover:bg-[#C5F3DD] text-[#047857] border border-[#6EE7B7] shadow-none" disabled>
                    <Spinner size="sm" className="mr-2" />
                    Loading
                  </Button>
                </div>
              </div>

              <Separator />

              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Warning</p>
                <p className="text-xs text-gray-500 mb-3">Caution actions, important notices (bg: #FEF3C7, text: #A16207, border: #FDE68A) - Size: 36px</p>
                <div className="flex flex-wrap gap-3">
                  <Button className="bg-[#FEF3C7] hover:bg-[#FDEDB5] text-[#A16207] border border-[#FDE68A] shadow-none">
                    <AlertCircle className="h-4 w-4 mr-2" />
                    Warning
                  </Button>
                  <Button className="bg-[#FEF3C7] hover:bg-[#FDEDB5] text-[#A16207] border border-[#FDE68A] shadow-none">Normal</Button>
                  <Button className="bg-[#FEF3C7] hover:bg-[#FDEDB5] text-[#A16207] border border-[#FDE68A] shadow-none" disabled>Disabled</Button>
                  <Button className="bg-[#FEF3C7] hover:bg-[#FDEDB5] text-[#A16207] border border-[#FDE68A] shadow-none" disabled>
                    <Spinner size="sm" className="mr-2" />
                    Loading
                  </Button>
                </div>
              </div>

              <Separator />

              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Error / Delete</p>
                <p className="text-xs text-gray-500 mb-3">Delete, remove, dangerous actions. Solid red for modals/primary actions (bg-red-700, text-white) - Size: 36px</p>
                <div className="flex flex-wrap gap-3">
                  <Button className="bg-red-700 hover:bg-red-800 text-white">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                  <Button className="bg-red-700 hover:bg-red-800 text-white">Remove</Button>
                  <Button className="bg-red-700 hover:bg-red-800 text-white" disabled>Disabled</Button>
                  <Button className="bg-red-700 hover:bg-red-800 text-white" disabled>
                    <Spinner size="sm" className="mr-2" />
                    Deleting...
                  </Button>
                </div>
              </div>

              <Separator />

              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Info</p>
                <p className="text-xs text-gray-500 mb-3">Informational actions, links (bg: #EDE9FE, text: #290BD9, border: #C4B5FD) - Size: 36px</p>
                <div className="flex flex-wrap gap-3">
                  <Button className="bg-[#EDE9FE] hover:bg-[#E1DDFC] text-[#290BD9] border border-[#C4B5FD] shadow-none">
                    <Info className="h-4 w-4 mr-2" />
                    Learn More
                  </Button>
                  <Button className="bg-[#EDE9FE] hover:bg-[#E1DDFC] text-[#290BD9] border border-[#C4B5FD] shadow-none">Normal</Button>
                  <Button className="bg-[#EDE9FE] hover:bg-[#E1DDFC] text-[#290BD9] border border-[#C4B5FD] shadow-none" disabled>Disabled</Button>
                  <Button className="bg-[#EDE9FE] hover:bg-[#E1DDFC] text-[#290BD9] border border-[#C4B5FD] shadow-none" disabled>
                    <Spinner size="sm" className="mr-2" />
                    Loading
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Delete Action Pattern */}
          <Card className="rounded-lg border bg-white mb-6">
            <CardHeader className="p-6 pb-0">
              <CardTitle className="text-lg font-semibold text-gray-900">Delete Action Pattern</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Standard Delete Icon</p>
                <p className="text-xs text-gray-500 mb-3">
                  Always visible (not hover-only). Uses Trash2 icon. Gray by default, red on hover.
                  Must trigger a ConformationModal for confirmation before action.
                </p>
                <div className="flex flex-wrap items-center gap-6">
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500">Default:</span>
                    <button className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500">Hover:</span>
                    <button className="p-1 text-red-600 bg-red-50 rounded cursor-pointer">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="mt-3 bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-mono text-gray-600">
                    className=&quot;p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer&quot;
                  </p>
                </div>
              </div>

              <Separator />

              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Delete Confirmation Button (in modals)</p>
                <p className="text-xs text-gray-500 mb-3">
                  Used inside ConformationModal as the confirm action. Solid red, white text.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button className="bg-red-700 hover:bg-red-800 text-white">
                    Remove
                  </Button>
                  <Button className="bg-red-700 hover:bg-red-800 text-white">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                  <Button className="bg-red-700 hover:bg-red-800 text-white" disabled>
                    <Spinner size="sm" className="mr-2" />
                    Deleting...
                  </Button>
                </div>
              </div>

              <Separator />

              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Usage Rules</p>
                <ul className="text-xs text-gray-600 space-y-1 list-disc ml-4">
                  <li>Delete icon is <strong>always visible</strong> — never hidden behind hover state</li>
                  <li>Always use <strong>Trash2</strong> icon from lucide-react</li>
                  <li>Always confirm via <strong>ConformationModal</strong> before executing delete</li>
                  <li>In dropdowns, use <strong>text-red-600</strong> for destructive menu items</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Header Toolbar Buttons */}
          <Card className="rounded-lg border bg-white mb-6">
            <CardHeader className="p-6 pb-0">
              <CardTitle className="text-lg font-semibold text-gray-900">Header Toolbar Buttons</CardTitle>
              <p className="text-gray-900">All header toolbar elements must be h-9 (36px) with text-gray-900. Used for filter, export, and action buttons in page headers.</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Filter Button (icon only)</p>
                <p className="text-xs text-gray-500 mb-3">variant="outline" h-9 w-9 p-0 text-gray-900</p>
                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" className="h-9 w-9 p-0 text-gray-900 relative">
                    <Filter className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" className="h-9 w-9 p-0 text-gray-900 relative">
                    <Filter className="h-4 w-4" />
                    <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-[#290BD9] text-white text-[10px] flex items-center justify-center">
                      2
                    </span>
                  </Button>
                </div>
              </div>

              <Separator />

              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Export Button</p>
                <p className="text-xs text-gray-500 mb-3">variant="outline" h-9 text-gray-900</p>
                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" className="h-9 text-gray-900">
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                  <Button variant="outline" className="h-9 text-gray-900" disabled>
                    <Spinner size="sm" className="mr-2" />
                    Exporting...
                  </Button>
                </div>
              </div>

              <Separator />

              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Action Buttons</p>
                <p className="text-xs text-gray-500 mb-3">variant="outline" h-9 text-gray-900</p>
                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" className="h-9 text-gray-900">
                    <Plus className="h-4 w-4 mr-2" />
                    Add
                  </Button>
                  <Button variant="outline" className="h-9 text-gray-900">
                    <Search className="h-4 w-4 mr-2" />
                    Search
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Other Buttons */}
          <Card className="rounded-lg border bg-white">
            <CardHeader className="p-6 pb-0">
              <CardTitle className="text-lg font-semibold text-gray-900">Other Buttons</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Icon Only</p>
                <p className="text-xs text-gray-500 mb-3">Compact buttons for toolbars and action menus</p>
                <div className="flex flex-wrap gap-3">
                  <Button size="icon" className="bg-[#290BD9] hover:bg-[#2309C5] text-white">
                    <Settings className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="outline">
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" className="text-gray-400 hover:text-red-600 hover:bg-red-50">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="destructive">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <Separator />

              <div>
                <p className="text-sm font-medium text-gray-700 mb-1">Google Button</p>
                <div className="w-[280px]">
                  <GoogleButton onClick={() => {}} />
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Attention Badges */}
        <section>
          <Card>
            <CardHeader>
              <CardTitle>Attention Badges</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-sm text-gray-600">
                Used to indicate items needing user action. Matches the notification bell style (<code className="text-xs bg-gray-100 px-1 py-0.5 rounded">bg-[#B91C1C]</code>).
              </p>
              <Separator />
              <div className="flex items-center gap-8">
                <div className="flex flex-col items-center gap-2">
                  <p className="text-sm font-medium text-gray-700">Badge (default)</p>
                  <AttentionBadge />
                  <code className="text-xs text-gray-500">variant="badge"</code>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <p className="text-sm font-medium text-gray-700">Dot</p>
                  <AttentionBadge variant="dot" />
                  <code className="text-xs text-gray-500">variant="dot"</code>
                </div>
              </div>
              <Separator />
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-700">Usage examples</p>
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-900">On nav items</span>
                    <AttentionBadge />
                  </div>
                  <div className="relative inline-block">
                    <div className="h-9 w-9 rounded-full bg-[#290BD9]/10 flex items-center justify-center text-[#290BD9] font-medium">M</div>
                    <AttentionBadge className="absolute -top-0.5 -right-0.5" />
                  </div>
                  <div className="relative inline-block">
                    <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center">
                      <AlertCircle className="h-5 w-5 text-gray-500" />
                    </div>
                    <AttentionBadge variant="dot" className="absolute -top-0.5 -right-0.5" />
                  </div>
                </div>
              </div>
              <Separator />
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Import</p>
                <code className="text-xs bg-gray-100 px-3 py-2 rounded block text-gray-700">
                  {`import { AttentionBadge } from '@/components/glitch-ui/attention-badge';`}
                </code>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}
